import React, { useMemo, useState } from 'react';
import { FixedSizeList, ListChildComponentProps } from 'react-window';
import styled from 'styled-components';

interface IProps {
    headers: string[];
    data: any[][];   // each row: [...cells, rowModel] (last element is the model object)
    cellFormatter?: (columnName: string, cellData: any, rowModel: any) => React.ReactNode;
    sortIndex?: number;
    sortDirection?: string;
    // optional grid-template-columns override (e.g. when reused for a table with a different column count)
    gridColumns?: string;
}

const ROW_HEIGHT = 38;
const MAX_HEIGHT = 700;
// default column template for header + rows (campaign stats: 4 columns).
// Email takes the flexible remaining width; the date/status columns are fixed (they need less),
// which gives the long "email - company name" values much more room before truncating.
// Callers can override via the gridColumns prop (consumed through the --vt-cols CSS var).
const GRID_COLUMNS = 'minmax(320px, 1fr) 210px 210px 140px';

const Wrapper = styled.div`
    .vt-header, .vt-row {
        display: grid;
        grid-template-columns: var(--vt-cols, ${GRID_COLUMNS});
        align-items: center;
        column-gap: 8px;
    }
    /* cyan header bar matching the global <table> th style (which a div-grid doesn't inherit) */
    .vt-header {
        background: rgb(0, 181, 209);
        color: #fff;
        font-weight: 700;
        font-size: 0.75rem;
    }
    .vt-header > div {
        cursor: pointer;
        user-select: none;
        padding: 0.4rem 6px;
        text-align: center;
    }
    .vt-row {
        border-bottom: 1px solid #eee;
    }
    /* match the prod table's 12px body font (the div-grid doesn't inherit it); badges inherit this */
    .vt-row > div {
        padding: 4px 6px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-size: 0.75rem;
    }
`;

// Mirrors SortableTable's comparator (number / Date / string-localeCompare) so sort order is unchanged.
const comparator = (sortIndex: number, sortOrder: string) => (a: any[], b: any[]) => {
    const x = a[sortIndex];
    const y = b[sortIndex];
    const dir = sortOrder === 'asc' ? 1 : -1;
    if (typeof x === 'number' && typeof y === 'number') return (x - y) * dir;
    if (x instanceof Date && y instanceof Date) return (x.getTime() - y.getTime()) * dir;
    return String(x ?? '').localeCompare(String(y ?? '')) * dir;
};

interface RowData {
    rows: any[][];
    headers: string[];
    cellFormatter?: IProps['cellFormatter'];
}

// Only the ~visible rows are mounted; memoized so scrolling re-renders just the new rows.
const Row = React.memo(({ index, style, data }: ListChildComponentProps<RowData>) => {
    const { rows, headers, cellFormatter } = data;
    const row = rows[index];
    const model = row[row.length - 1];
    return (
        <div className="vt-row" style={style}>
            {headers.map((h, j) => {
                // A formatter may choose to format only some columns; when it returns null/undefined
                // for a column we fall back to the raw cell value rather than rendering an empty cell.
                const formatted = cellFormatter ? cellFormatter(h, row[j], model) : undefined;
                return <div key={j}>{formatted ?? <>{row[j]}</>}</div>;
            })}
        </div>
    );
});

const VirtualizedStatsTable: React.FC<IProps> = (props: IProps) => {
    const { headers, data, cellFormatter } = props;
    const [sortIndex, setSortIndex] = useState(props.sortIndex ?? 0);
    const [sortOrder, setSortOrder] = useState(props.sortDirection ?? 'asc');

    const sortedData = useMemo(
        () => [...data].sort(comparator(sortIndex, sortOrder)),
        [data, sortIndex, sortOrder]
    );

    const onHeaderClicked = (i: number) => {
        if (i === sortIndex) {
            setSortOrder(o => (o === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortIndex(i);
            setSortOrder('asc');
        }
    };

    const height = Math.min(sortedData.length * ROW_HEIGHT, MAX_HEIGHT);

    return (
        <Wrapper style={props.gridColumns ? ({ ['--vt-cols' as any]: props.gridColumns }) : undefined}>
            <div className="vt-header">
                {headers.map((h, i) => (
                    <div key={i} onClick={() => onHeaderClicked(i)}>
                        {h}{sortIndex === i && (sortOrder === 'asc' ? ' ▲' : ' ▼')}
                    </div>
                ))}
            </div>
            <FixedSizeList
                height={height}
                width="100%"
                itemCount={sortedData.length}
                itemSize={ROW_HEIGHT}
                itemData={{ rows: sortedData, headers, cellFormatter }}
                overscanCount={8}
            >
                {Row}
            </FixedSizeList>
        </Wrapper>
    );
};

export default VirtualizedStatsTable;
